import { Injectable } from '@angular/core';

export interface MarkerLayers {
  default: string;
  selected: string;
  unselected: string;
  invisible: string;
  unclustered: string;
  unclusteredInvisible: string;
  clustered: string;
  clusteredCount: string;
  unclusteredSpiderfy: string;
  /**
   * The stops a focus has pushed away, drawn as small dots rather than pins. A `circle` layer
   * on the unclustered source, so it holds every stop the map has whatever grouping says, and
   * so it takes no part in symbol collision — a dot can never cull the lit pin beside it.
   * Which stops it draws is decided entirely by feature state.
   */
  dim: string;
  /**
   * The stop numbers of the single route on display, one label per marker feature. On the
   * unclustered source, like `dim` and for the same reason: it is the only source holding every
   * stop, so the numbers survive grouping. Which stops carry one is decided by feature state.
   */
  sequence: string;
}

export interface MarkerSource {
  clustered: string;
  spiderfy: string;
  default: string;
}

export interface RouteLayers {
  default: string;
  /** The white halo drawn under a focused route, transparent until a focus lights it. */
  casing: string;
  invisible: string;
  symbols: string;
  dashed: string;
}

export interface RouteSource {
  default: string;
}

export interface AreaLayers {
  fill: string;
  outline: string;
}

export interface AreaSource {
  default: string;
}

export interface MapRegistry {
  markers: {
    layers: MarkerLayers;
    source: MarkerSource;
  };
  routes: {
    layers: RouteLayers;
    source: RouteSource;
  };
  areas: {
    layers: AreaLayers;
    source: AreaSource;
  };
}

@Injectable({
  providedIn: 'root'
})
export class MapRegistryService {
  private registry: MapRegistry | null = null;

  init(names: {
    markerLayerName: string;
    markerSourceName: string;
    routeLayerName: string;
    routeSourceName: string;
    areaLayerName: string;
    areaSourceName: string;
  }): void {
    if (this.registry) {
      console.warn('MapRegistryService already initialized');
      return;
    }

    const {
      markerLayerName,
      markerSourceName,
      routeLayerName,
      routeSourceName,
      areaLayerName,
      areaSourceName
    } = names;

    this.registry = {
      markers: {
        layers: {
          default: markerLayerName,
          selected: `${markerLayerName}-selected`,
          unselected: `${markerLayerName}-unselected`,
          invisible: `${markerLayerName}-invisible`,
          unclustered: `${markerLayerName}-unclustered`,
          unclusteredInvisible: `${markerLayerName}-unclustered-invisible`,
          clustered: `${markerLayerName}-groups`,
          clusteredCount: `${markerLayerName}-groups-count`,
          unclusteredSpiderfy: `${markerLayerName}-unclustered-spiderfy`,
          dim: `${markerLayerName}-dim`,
          sequence: `${markerLayerName}-sequence`
        },
        source: {
          clustered: `${markerSourceName}-clustered`,
          spiderfy: `${markerSourceName}-spiderfy`,
          default: markerSourceName
        }
      },
      routes: {
        layers: {
          default: routeLayerName,
          casing: `${routeLayerName}-casing`,
          invisible: `${routeLayerName}-invisible`,
          symbols: `${routeLayerName}-symbols`,
          dashed: `${routeLayerName}-dashed`
        },
        source: {
          default: routeSourceName
        }
      },
      areas: {
        layers: {
          fill: `${areaLayerName}-fill`,
          outline: `${areaLayerName}-outline`
        },
        source: {
          default: areaSourceName
        }
      }
    };
  }

  /**
   * Get the complete registry of all layers and sources. Throws an error if the registry
   * hasn't been initialized.
   *
   * @returns {MapRegistry}
   */
  getRegistry(): MapRegistry {
    if (!this.registry) {
      throw new Error(
        'MapRegistryService not initialized. Call init() first in MapboxComponent.'
      );
    }
    return this.registry;
  }

  /**
   * Get marker layers only
   *
   * @returns {MarkerLayers}
   */
  getMarkerLayers(): MarkerLayers {
    return this.getRegistry().markers.layers;
  }

  /**
   * Get route layers only
   *
   * @returns {RouteLayers}
   */
  getRouteLayers(): RouteLayers {
    return this.getRegistry().routes.layers;
  }

  /**
   * Get marker sources only
   *
   * @returns {MarkerSource}
   */
  getMarkerSources(): MarkerSource {
    return this.getRegistry().markers.source;
  }

  /**
   * Get route sources only
   *
   * @returns {RouteSource}
   */
  getRouteSources(): RouteSource {
    return this.getRegistry().routes.source;
  }

  /**
   * Get area layers only
   *
   * @returns {AreaLayers}
   */
  getAreaLayers(): AreaLayers {
    return this.getRegistry().areas.layers;
  }

  /**
   * Get area sources only
   *
   * @returns {AreaSource}
   */
  getAreaSources(): AreaSource {
    return this.getRegistry().areas.source;
  }

  /**
   * Get all layer names as a flat array
   *
   * @returns {string[]}
   */
  getAllLayerNames(): string[] {
    const reg = this.getRegistry();
    return [
      ...Object.values(reg.markers.layers),
      ...Object.values(reg.routes.layers),
      ...Object.values(reg.areas.layers)
    ];
  }

  /**
   * Get all source names as a flat array
   *
   * @returns {string[]}
   */
  getAllSourceNames(): string[] {
    const reg = this.getRegistry();
    return [
      ...Object.values(reg.markers.source),
      ...Object.values(reg.routes.source),
      ...Object.values(reg.areas.source)
    ];
  }

  /**
   * Check if a layer name exists in the registry
   *
   * @param {string} layerName - The name of the layer to check
   * @returns {boolean}
   */
  hasLayer(layerName: string): boolean {
    return this.getAllLayerNames().includes(layerName);
  }

  /**
   * Check if a source name exists in the registry
   *
   * @param {string} sourceName - The name of the source to check
   * @returns {boolean}
   */
  hasSource(sourceName: string): boolean {
    return this.getAllSourceNames().includes(sourceName);
  }

  /** Reset the registry */
  reset(): void {
    this.registry = null;
  }
}
